/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react'
import debounce from 'lodash/debounce'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Button from '@mui/material/Button'
import { DEBOUNCE_DELAY } from 'constants/consts'
import {
  CREATE_QUESTION_PASSAGE,
  GET_ONE_QUESTION_PASSAGE,
  SEARCH_TEMPLATE_PASSAGE_QUESTION,
} from 'queries/questionPassage'
import {
  findOneQuestionPassage,
  findOneQuestionPassage_findOneQuestionPassage as QuestionPassage,
  findOneQuestionPassageVariables,
} from '__generatedTypes__/findOneQuestionPassage'
import { CreateQuestionPassageInput } from '__generatedTypes__/globalTypes'
import { createCARSQuestion, createCARSQuestionVariables } from '__generatedTypes__/createCARSQuestion'
import {
  searchTemplatePassageQuestion,
  searchTemplatePassageQuestionVariables,
  searchTemplatePassageQuestion_searchTemplatePassageQuestion_items as Template,
} from '__generatedTypes__/searchTemplatePassageQuestion'
import Input from 'components/Input'
import Select from 'components/Select'
import { useHistory } from 'hooks/useHistory'
import { toast } from 'utils/toast'
import * as classes from '../../../Questions/Form/TemplateForm/styles'

export type TemplateFormProps = {
  setTemplateData: (template: QuestionPassage) => void
  getTemplateData: (templateName: string) => Promise<CreateQuestionPassageInput>
  isLoadTemplateDisabled?: boolean
}

export const TemplateForm = ({ setTemplateData, getTemplateData, isLoadTemplateDisabled }: TemplateFormProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [templateName, setTemplateName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { populateHistoryWithUser } = useHistory()
  const [createTemplate] = useMutation<createCARSQuestion, createCARSQuestionVariables>(CREATE_QUESTION_PASSAGE)
  const [loadTemplate] = useLazyQuery<findOneQuestionPassage, findOneQuestionPassageVariables>(GET_ONE_QUESTION_PASSAGE)
  const templatesState = useQuery<searchTemplatePassageQuestion, searchTemplatePassageQuestionVariables>(
    SEARCH_TEMPLATE_PASSAGE_QUESTION
  )
  const templates = templatesState.data?.searchTemplatePassageQuestion.items ?? []

  const handleTemplateInputChange = useCallback(
    debounce((searchString: string) => {
      templatesState.refetch({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const handleLoadTemplateClick = useCallback(async () => {
    if (!selectedTemplate || submitting) {
      return
    }

    setSubmitting(true)
    const { data } = await loadTemplate({
      variables: {
        params: selectedTemplate.id,
      },
    })
    data?.findOneQuestionPassage && setTemplateData(data.findOneQuestionPassage)
    setSubmitting(false)
  }, [setTemplateData, submitting, loadTemplate, selectedTemplate])

  const handleSaveTemplateClick = useCallback(async () => {
    if (!templateName || submitting) {
      return
    }

    setSubmitting(true)
    try {
      const templateData = await getTemplateData(templateName)
      await createTemplate({
        variables: {
          params: {
            ...templateData,
            history: populateHistoryWithUser(),
          },
        },
      })
      await templatesState.refetch()

      toast.success('Template saved successfully')
    } catch (error) {
      toast.error('Failed to save template')
    }
    setSubmitting(false)
  }, [getTemplateData, submitting, createTemplate, templateName, templatesState.refetch])

  return (
    <>
      <h4>Template</h4>
      <Select<Template>
        value={selectedTemplate}
        items={templates}
        onChange={setSelectedTemplate}
        onInputChange={handleTemplateInputChange}
        getItemLabel={(template) => template.templateName ?? 'No name'}
        renderItem={(template) => template.templateName ?? 'No name'}
        loading={templatesState.loading}
        disabled={isLoadTemplateDisabled}
        placeholder="Choose Template"
        displaySelectedItemsFirst
        searchable
        fullWidth
      />
      <Button
        variant="outlined"
        color="blue"
        type="button"
        onClick={handleLoadTemplateClick}
        disabled={!selectedTemplate || submitting || isLoadTemplateDisabled}
      >
        Load Template
      </Button>
      <div css={classes.divider} />
      <Input
        value={templateName}
        onChange={setTemplateName}
        label="Save as Template"
        placeholder="Enter Template Title"
        fullWidth
      />
      <Button
        variant="outlined"
        color="blue"
        type="button"
        onClick={handleSaveTemplateClick}
        disabled={!templateName || submitting}
      >
        Save
      </Button>
    </>
  )
}
